import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { useFetch } from "../../../../hooks/useFetch";
import { Device, DeviceById } from "../../../../types/types";
import _ from "lodash";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../../../../ui/DataTable/DataTable";
import { Separator } from "../../../../ui/Separator";
import EditDevice from "./EditDevice";
import { SensorsTableData } from "../../../../types/formDataTypes";

type Props = {
  id: string | undefined;
  currentDevice: Device | undefined;
};

const DeviceConstantsTab = ({ id, currentDevice }: Props) => {
  const [deviceData, setDeviceData] = useState<DeviceById>();

  let [isEditOpen, setIsEditOpen] = useState(false);
  let [currentItemId, setCurrentItemId] = useState("");
  
  function openEditModal() {
    setIsEditOpen(true);
  }

  function closeEditModal() {
    setIsEditOpen(false);
    setCurrentItemId("");
  }
  const onItemEdit = (id: string) => {
    setCurrentItemId(id);
    openEditModal();
  };

  const [{ response: itemUpdateResponse, isLoading: itemUpdateIsLoading, error: itemUpdateError }, doFetchItemUpdate] =
    useFetch(`/devices/devices/${id}`);
  
  const refreshItems = () => {
    doFetchItemUpdate();
  };

  useEffect(() => {
    doFetchItemUpdate();
  }, [id]);

  useEffect(() => {
    if (itemUpdateResponse) {
      const newDeviceData = itemUpdateResponse.data as unknown as DeviceById;
      setDeviceData(newDeviceData);
    }
  }, [itemUpdateResponse]);

  const nodes = deviceData?.attributes.slots.flatMap((data) => {
    const slotNumber = data.number;
    return data.sensors.flatMap((sensor): SensorsTableData[] => {
      if (sensor.id === undefined) {
        return [];
      }

      return [{
        id: sensor.id,
        number: slotNumber,
        idle_level: sensor.idle_level,
        turned_off_level: sensor.turned_off_level,
        factor: sensor.factor,
        power_factor: sensor.power_factor,
        sensor_type_name: t(sensor.sensor_type.data_key_name),
      }];
    });
  }) ?? [];

  const data: SensorsTableData[] = _.sortBy(nodes, []);

  const columns: ColumnDef<SensorsTableData>[] = [
    {
      header: t("title_name"),
      cell: ({ row }) => row.original.sensor_type_name,
    },
    {
      header: t("slot"),
      cell: ({ row }) => row.original.number,
    },
    {
      header: t("constant"),
      cell: ({ row }) => row.original.factor,
    },
    {
      header: t("idle_level"),
      cell: ({ row }) => row.original.idle_level,
    },
    {
      header: t("turned_off_level"),
      cell: ({ row }) => row.original.turned_off_level,
    },
    {
      header: t("last_activity"),
      cell: ({ row }) => "",
    },
  ];

  return (
    <div className="pt-8 flex flex-col w-full gap-8">
      {currentItemId != "" && (
        <EditDevice
          id={currentItemId}
          isEditOpen={isEditOpen}
          closeModal={closeEditModal}
          data={data}
          refreshItems={refreshItems}
        />
      )}
      <div className="flex flex-col">
        <div className="text-base font-medium">{t("sensors_on_device")}</div>
        <Separator className="flex-none" />
        <DataTable data={data} columns={columns} onClickRow={onItemEdit} />
      </div>
    </div>
  );
};

export default DeviceConstantsTab;
